import { ClientDetail } from 'components/apiClient/ClientDetail/ClientDetail';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { Table } from '@fragment/ui/src/components/Table';

import type { APIClientNode } from '../../../apiClient/types';
import { useGetWorkspaceApiClientQuery } from '../../../../hooks/internalApi';
import { useHasWorkspacePermission } from '../../../../hooks/useHasWorkspacePermission';
import { useInternalApiContext } from '../../../../hooks/useInternalApiContext';
import { getResultOrError } from '../../../../utils/errors';
import { useWorkspaceId } from '../../../../wrappers/WorkspaceProvider';
import {
  type ApiClient,
  ApiClientForm,
} from '../../../apiClient/NewApiClient/ApiClientForm';
import { TableLoadError } from '../../../TableLoadError/TableLoadError';
import { SettingsModalLayout } from './SettingsModalLayout';

const schema = [
  {
    title: 'Name',
    key: 'clientName',
  },
  {
    title: 'ID',
    key: 'id',
    justify: 'right',
  },
] as const;

const emptyValue = {
  ID: 'N/A',
  Name: 'No API clients yet',
};

export const ApiClientsSettingsForm = ({
  hideHeader = false,
  onAction,
}: {
  hideHeader?: boolean;
  onAction?: (
    action: { title: string; onClick: () => void } | undefined
  ) => void;
} = {}) => {
  const workspaceId = useWorkspaceId();
  const canCreate = useHasWorkspacePermission(Permissions.api_client.create);
  const { context } = useInternalApiContext();
  // Stable reference so urql doesn't re-issue the query on every render.
  const variables = useMemo(() => ({ workspaceId }), [workspaceId]);
  const [{ data, error, fetching }, refetchApiClients] =
    useGetWorkspaceApiClientQuery({
      variables,
      context,
      pause: !context.token,
    });
  const { isError: isWorkspaceError, result: workspace } = getResultOrError(
    data?.workspace,
    error
  );
  const { isError: isApiClientsError, result: apiClients } = getResultOrError(
    workspace?.apiClients,
    error
  );

  const [selectedClient, setSelectedClient] = useState<
    APIClientNode | undefined
  >(undefined);
  const [createdSecret, setCreatedSecret] = useState<string | undefined>(
    undefined
  );
  const [isCreatingClient, setIsCreatingClient] = useState(false);

  const refresh = useCallback(
    () => refetchApiClients({ requestPolicy: 'network-only' }),
    [refetchApiClients]
  );

  const onCreate = useCallback(
    (client: ApiClient) => {
      refresh();
      setSelectedClient({
        __typename: 'ApiClient',
        id: client.client.id,
        clientName: client.client.clientName,
        role: client.client.role,
      });
      setCreatedSecret(client.secret);
      setIsCreatingClient(false);
    },
    [refresh]
  );

  const onRowClick = useCallback((client: APIClientNode) => {
    setCreatedSecret(undefined);
    setSelectedClient(client);
  }, []);
  const isError = isWorkspaceError || isApiClientsError || !data;

  const onCancel = useCallback(() => {
    setSelectedClient(undefined);
    setCreatedSecret(undefined);
    setIsCreatingClient(false);
  }, []);

  const onConfirmedDelete = useCallback(() => {
    setSelectedClient(undefined);
    setCreatedSecret(undefined);
    refresh();
  }, [refresh]);

  const onRoleUpdated = useCallback(
    (client: APIClientNode) => {
      setSelectedClient(client);
      refresh();
    },
    [refresh]
  );

  // Error outranks detail on purpose: a failed list query replaces the
  // detail view even when a freshly created secret is on screen. Losing the
  // one-time secret is acceptable since the user can just create another
  // client, and it keeps a broken list from masquerading as a working screen.
  const viewState: 'list' | 'error' | 'detail' | 'new' = match([
    selectedClient,
    isError,
    isCreatingClient,
  ])
    .with([undefined, P._, true], () => 'new' as const)
    .with([P._, true, P._], () => 'error' as const)
    .with([P.not(undefined), false, P._], () => 'detail' as const)
    .with([undefined, false, false], () => 'list' as const)
    .exhaustive();

  // Publishes the list view's primary action to the parent so hosts that hide
  // the built-in header (e.g. PaymentsSettingsPage) can render it themselves.
  useEffect(() => {
    if (!onAction) return;
    onAction(
      viewState === 'list' && canCreate
        ? {
            title: 'New Client +',
            onClick: () => setIsCreatingClient(true),
          }
        : undefined
    );
  }, [viewState, onAction, canCreate]);

  return (
    <SettingsModalLayout
      hideHeader={hideHeader}
      title={match(viewState)
        .with('new', () => 'Create API Client')
        .with('detail', () => 'API Client Details')
        .with('list', () => 'API Clients')
        .with('error', () => 'Failed to load API Clients')
        .exhaustive()}
      onBackButtonClick={
        viewState === 'detail' || viewState === 'new' ? onCancel : undefined
      }
      topButtonSection={
        viewState === 'list' && !hideHeader && canCreate ? (
          <PrimaryButtonV2
            onClick={() => setIsCreatingClient(true)}
            secondaryIcon="+"
          >
            New Client
          </PrimaryButtonV2>
        ) : null
      }
    >
      {match(viewState)
        .with('error', () => (
          <TableLoadError
            entityColumnName="Name"
            rightColumnName="ID"
            {...{ error, retry: refresh, fetching }}
          />
        ))
        .with('list', () => (
          <Table
            textSize="md"
            data={apiClients?.nodes ?? []}
            onRowClick={onRowClick}
            schema={schema}
            emptyValue={emptyValue}
            borderedRows
          />
        ))
        .with('detail', () =>
          // Keyed by client id so tab state resets when the selected client
          // changes (the component is otherwise reused). The created flow
          // shows the secret + Close; the existing flow shows Delete.
          createdSecret !== undefined ? (
            <ClientDetail
              key={selectedClient!.id}
              apiClient={selectedClient!}
              clientSecret={createdSecret}
              initialTab="string"
              onClose={onCancel}
              onRoleUpdated={onRoleUpdated}
            />
          ) : (
            <ClientDetail
              key={selectedClient!.id}
              apiClient={selectedClient!}
              onConfirmedDelete={onConfirmedDelete}
              onRoleUpdated={onRoleUpdated}
            />
          )
        )
        .with('new', () => (
          <ApiClientForm key="form" onCancel={onCancel} onCreate={onCreate} />
        ))
        .exhaustive()}
    </SettingsModalLayout>
  );
};
